import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader } from 'lucide-react';
import {
  getLatestUsdaBulkMatchRun,
  getUsdaBulkMatchRun,
  listUsdaBulkMatchItems,
  applyUsdaBulkMatchReview,
  skipUsdaBulkMatchItem,
  type UsdaBulkItem,
  type UsdaBulkRun,
} from '../../../utils/unitConversionApi';

type Confidence = 'high' | 'medium' | 'low' | 'none';

const CONFIDENCE_LABELS: Record<Confidence, string> = {
  high: 'Élevée',
  medium: 'Moyenne',
  low: 'Faible',
  none: 'Aucune',
};

const CONFIDENCE_STYLES: Record<Confidence, string> = {
  high: 'bg-green-100 text-green-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-orange-100 text-orange-800',
  none: 'bg-gray-100 text-gray-600',
};

export function UsdaBulkMatchReviewPage() {
  const [searchParams] = useSearchParams();
  const runIdParam = searchParams.get('runId');
  const [run, setRun] = useState<UsdaBulkRun | null>(null);
  const [items, setItems] = useState<UsdaBulkItem[]>([]);
  const [confidenceFilter, setConfidenceFilter] = useState<Confidence | ''>('');
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async (preferredRunId?: string | null) => {
    setLoading(true);
    setError(null);
    try {
      let nextRun: UsdaBulkRun;
      if (preferredRunId) {
        nextRun = (await getUsdaBulkMatchRun(preferredRunId)).run;
      } else {
        const res = await getLatestUsdaBulkMatchRun();
        if (!res.run) throw new Error('Aucun run USDA disponible');
        nextRun = res.run;
      }
      setRun(nextRun);
      const itemsRes = await listUsdaBulkMatchItems(nextRun.id, {
        confidence: confidenceFilter || undefined,
        status: 'pending',
      });
      setItems(itemsRes.items);
      const defaults: Record<string, string> = {};
      for (const item of itemsRes.items) {
        if (item.suggestedPortionId) defaults[item.ingredientId] = item.suggestedPortionId;
      }
      setSelections(defaults);
    } catch (err) {
      setError((err as Error).message);
      setRun(null);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(runIdParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runIdParam]);

  useEffect(() => {
    if (!run?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const itemsRes = await listUsdaBulkMatchItems(run.id, {
          confidence: confidenceFilter || undefined,
          status: 'pending',
        });
        if (cancelled) return;
        setItems(itemsRes.items);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [confidenceFilter, run?.id]);

  const withBusy = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const counts = useMemo(() => run?.byConfidence || null, [run]);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/admin/mass-db/conversion"
          className="inline-flex items-center text-sm text-gray-500 hover:text-menioo-navy mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Retour conversion
        </Link>
        <h2 className="text-xl font-display font-bold text-menioo-navy">
          Review matching USDA (pièce)
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Validez chaque poids proposé. Rien n&apos;est écrit sans votre clic Appliquer.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {counts && (
        <div className="flex flex-wrap gap-2 text-sm">
          {(Object.keys(CONFIDENCE_LABELS) as Confidence[]).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setConfidenceFilter((prev) => (prev === c ? '' : c))}
              className={`px-3 py-1 rounded-full ${CONFIDENCE_STYLES[c]} ${
                confidenceFilter === c ? 'ring-2 ring-menioo-navy' : ''
              }`}
            >
              {CONFIDENCE_LABELS[c]}: {counts[c] ?? 0}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center text-sm text-gray-500">
          <Loader className="h-4 w-4 mr-2 animate-spin" />
          Chargement…
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">Aucun item en attente pour ce filtre.</p>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.ingredientId}
              className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-menioo-navy">{item.ingredientName}</span>
                  {item.nameEn && (
                    <span className="text-xs text-gray-500">EN: {item.nameEn}</span>
                  )}
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${CONFIDENCE_STYLES[item.confidence]}`}
                  >
                    {CONFIDENCE_LABELS[item.confidence]}
                  </span>
                </div>
                <select
                  value={selections[item.ingredientId] || ''}
                  onChange={(e) =>
                    setSelections((prev) => ({
                      ...prev,
                      [item.ingredientId]: e.target.value,
                    }))
                  }
                  className="mt-2 w-full max-w-xl rounded-md border-gray-300 text-sm"
                >
                  <option value="">— choisir —</option>
                  {item.candidates.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.descriptionEn} · {c.modifier} · {Math.round(c.unitWeight_g)}g (score{' '}
                      {c.score})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  disabled={!!busy || !selections[item.ingredientId]}
                  onClick={() =>
                    withBusy(`apply-${item.ingredientId}`, async () => {
                      if (!run) return;
                      await applyUsdaBulkMatchReview(
                        item.ingredientId,
                        selections[item.ingredientId],
                        run.id
                      );
                      setItems((prev) =>
                        prev.filter((x) => x.ingredientId !== item.ingredientId)
                      );
                    })
                  }
                  className="px-3 py-1.5 rounded-md text-sm text-white bg-menioo-green hover:bg-menioo-green-dark disabled:opacity-50"
                >
                  Appliquer
                </button>
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={() =>
                    withBusy(`skip-${item.ingredientId}`, async () => {
                      if (!run) return;
                      await skipUsdaBulkMatchItem(run.id, item.ingredientId);
                      setItems((prev) =>
                        prev.filter((x) => x.ingredientId !== item.ingredientId)
                      );
                    })
                  }
                  className="px-3 py-1.5 rounded-md text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Skip
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
